/**
 * Mirrors rocketlane-agentbuilder trigger registry helpers for preview save payloads.
 */
import {
  CanCreatePageOn,
  type DocumentationAgentPrompt,
} from './document-agent-prompt.types';
import type {
  DocumentationAgent,
  DocumentationAgentTriggerRegistryConfig,
  DocumentationAgentTriggerRegistryOperation,
} from 'api/document-agent/types';

export type {
  DocumentationAgentTriggerRegistryAction,
  DocumentationAgentTriggerRegistryConfig,
  DocumentationAgentTriggerRegistryOperation,
  DocumentationAgentTriggerRegistryTrigger,
} from 'api/document-agent/types';

export function newDocumentAgentActionUuid(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return `action-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const emptyRegistryConfig = (): DocumentationAgentTriggerRegistryConfig => ({
  triggers: [],
  actions: [],
});

/**
 * UI edits often replace trigger/action rows with partial objects (no `id` / `registryId`).
 * Re-attach persisted server fields from the last GET by matching `triggerUuid` / `actionUuid`,
 * then same index, so PUT/DELETE payloads include `id`, `registryId`, timestamps, `isDeleted`.
 */
function mergePersistedRegistryMetadata(
  draft: DocumentationAgentTriggerRegistryConfig,
  persisted: DocumentationAgentTriggerRegistryConfig | undefined,
): DocumentationAgentTriggerRegistryConfig {
  if (!persisted) {
    return draft;
  }

  const mergedTriggers = (draft.triggers ?? []).map((t, i) => {
    const byUuid =
      t.triggerUuid != null
        ? persisted.triggers?.find((p) => p.triggerUuid === t.triggerUuid)
        : undefined;
    const byIndex = persisted.triggers?.[i];
    const base = byUuid ?? byIndex;

    if (!base) {
      return t;
    }

    return {
      ...base,
      ...t,
      triggerCondition: t.triggerCondition ?? base.triggerCondition ?? {},
      input: t.input ?? base.input ?? {},
    };
  });

  const mergedActions = (draft.actions ?? []).map((a, i) => {
    const byUuid =
      a.actionUuid != null
        ? persisted.actions?.find((p) => p.actionUuid === a.actionUuid)
        : undefined;
    const byIndex = persisted.actions?.[i];
    const base = byUuid ?? byIndex;

    if (!base) {
      return a;
    }

    return {
      ...base,
      ...a,
      matchingCondition: a.matchingCondition ?? base.matchingCondition ?? {},
      input: {
        ...base.input,
        ...a.input,
      },
    };
  });

  return {
    ...persisted,
    ...draft,
    triggers: mergedTriggers,
    actions: mergedActions,
  };
}

/**
 * POST/PUT/DELETE body for `agentTriggerRegistryOperation`: same shape as GET
 * (`id`, `registryId`, timestamps on rows, root `placeholders`, etc.), except the
 * redundant root `trigger` duplicate from some GET responses is omitted.
 */
export function toAgentTriggerRegistryConfigWritePayload(
  cfg: DocumentationAgentTriggerRegistryConfig,
): DocumentationAgentTriggerRegistryConfig {
  const { trigger: _redundantSummary, ...topLevel } =
    cfg as DocumentationAgentTriggerRegistryConfig & { trigger?: unknown };

  return {
    ...topLevel,
    triggers: (cfg.triggers ?? []).map((t) => ({
      ...t,
      triggerCondition: t.triggerCondition ?? {},
      input: t.input ?? {},
    })),
    actions: (cfg.actions ?? []).map((a) => ({
      ...a,
      matchingCondition: a.matchingCondition ?? {},
      input: {
        ...a.input,
        documentAgentId: a.input?.documentAgentId ?? null,
      },
    })),
  };
}

function resolvePersistedRegistryIdForDelete(
  agent: DocumentationAgent,
): number | undefined {
  const cfg = agent.configuration.agentTriggerRegistryConfig;
  const id = cfg?.id ?? agent.configuration.agentTriggerRegistryId;

  return typeof id === 'number' ? id : undefined;
}

function registryConfigForDeleteOperation(
  existingAgent: DocumentationAgent,
): DocumentationAgentTriggerRegistryConfig {
  const persisted = existingAgent.configuration.agentTriggerRegistryConfig;
  const registryId = resolvePersistedRegistryIdForDelete(existingAgent);

  if (persisted) {
    return toAgentTriggerRegistryConfigWritePayload({
      ...persisted,
      ...(registryId != null && persisted.id == null ? { id: registryId } : {}),
      triggers: [],
      actions: [],
    });
  }

  return toAgentTriggerRegistryConfigWritePayload({
    ...(registryId != null ? { id: registryId } : {}),
    triggers: [],
    actions: [],
  });
}

/**
 * Persisted agents must send the real id in invoke action input; UI state may use -1.
 */
function applyDocumentAgentIdToRegistryConfig(
  cfg: DocumentationAgentTriggerRegistryConfig,
  agentId: number,
): DocumentationAgentTriggerRegistryConfig {
  if (!cfg.actions?.length) {
    return cfg;
  }

  return {
    ...cfg,
    actions: cfg.actions.map((action, index) =>
      index === 0
        ? {
            ...action,
            input: {
              ...action.input,
              documentAgentId: agentId,
            },
          }
        : action,
    ),
  };
}

function resolveAgentIdForRegistry(
  prompt: DocumentationAgentPrompt,
  existingAgent: DocumentationAgent,
): number | null {
  if (typeof existingAgent.id === 'number' && existingAgent.id > 0) {
    return existingAgent.id;
  }
  if (typeof prompt.agentId === 'number' && prompt.agentId > 0) {
    return prompt.agentId;
  }

  return null;
}

export function serializeAutomationRegistryBaseline(
  prompt: Pick<
    DocumentationAgentPrompt,
    'agentTriggerRegistryConfig' | 'actionEnabled' | 'canCreatePageOn'
  >,
): string {
  return JSON.stringify({
    canCreatePageOn: prompt.canCreatePageOn,
    agentTriggerRegistryConfig: prompt.agentTriggerRegistryConfig ?? null,
    actionEnabled: prompt.actionEnabled ?? true,
  });
}

function hasAutomationTriggerPayload(
  prompt: DocumentationAgentPrompt,
): boolean {
  if (prompt.canCreatePageOn !== CanCreatePageOn.OnEvent) {
    return false;
  }
  if (prompt.actionEnabled === false) {
    return false;
  }

  return (prompt.agentTriggerRegistryConfig?.triggers?.length ?? 0) > 0;
}

export function resolveAgentTriggerRegistryOperationForCreate(
  prompt: DocumentationAgentPrompt,
): DocumentationAgentTriggerRegistryOperation {
  if (!hasAutomationTriggerPayload(prompt)) {
    return null;
  }

  return {
    operation: 'POST',
    agentTriggerRegistryConfig: toAgentTriggerRegistryConfigWritePayload(
      prompt.agentTriggerRegistryConfig ?? emptyRegistryConfig(),
    ),
  };
}

export function resolveAgentTriggerRegistryOperationForUpdate(
  prompt: DocumentationAgentPrompt,
  existingAgent: DocumentationAgent,
): DocumentationAgentTriggerRegistryOperation {
  const wasAutomation =
    existingAgent.configuration.creationTrigger === 'AUTOMATION';
  const nowAutomation = prompt.canCreatePageOn === CanCreatePageOn.OnEvent;
  const agentId = resolveAgentIdForRegistry(prompt, existingAgent);

  const persistedRegistry =
    existingAgent.configuration.agentTriggerRegistryConfig ?? undefined;
  const mergedRegistry = mergePersistedRegistryMetadata(
    prompt.agentTriggerRegistryConfig ?? emptyRegistryConfig(),
    persistedRegistry,
  );

  const withAgentId = (
    cfg: DocumentationAgentTriggerRegistryConfig,
  ): DocumentationAgentTriggerRegistryConfig =>
    agentId != null ? applyDocumentAgentIdToRegistryConfig(cfg, agentId) : cfg;

  const opConfig = (
    cfg: DocumentationAgentTriggerRegistryConfig,
  ): DocumentationAgentTriggerRegistryConfig =>
    toAgentTriggerRegistryConfigWritePayload(withAgentId(cfg));

  if (wasAutomation && !nowAutomation) {
    return {
      operation: 'DELETE',
      agentTriggerRegistryConfig:
        registryConfigForDeleteOperation(existingAgent),
    };
  }

  if (!wasAutomation && nowAutomation) {
    if (!hasAutomationTriggerPayload(prompt)) {
      return null;
    }
    if (agentId == null) {
      return null;
    }

    return {
      operation: 'POST',
      agentTriggerRegistryConfig: opConfig(mergedRegistry),
    };
  }

  if (wasAutomation && nowAutomation) {
    if (!hasAutomationTriggerPayload(prompt)) {
      return {
        operation: 'DELETE',
        agentTriggerRegistryConfig:
          registryConfigForDeleteOperation(existingAgent),
      };
    }
    if (agentId == null) {
      return null;
    }

    const baseline = prompt.automationRegistryBaseline;
    const current = serializeAutomationRegistryBaseline(prompt);

    if (baseline != null && current === baseline) {
      return null;
    }

    return {
      operation: 'PUT',
      agentTriggerRegistryConfig: opConfig(mergedRegistry),
    };
  }

  return null;
}

export function mergePromptFromAgentTriggerRegistry(
  agent: DocumentationAgent,
  base: DocumentationAgentPrompt,
): Partial<DocumentationAgentPrompt> {
  const cfg =
    agent.configuration.agentTriggerRegistryConfig ??
    agent.configuration.agentTriggerRegistryOperation
      ?.agentTriggerRegistryConfig ??
    null;

  if (!cfg?.triggers?.length) {
    return {};
  }

  return {
    agentTriggerRegistryConfig: cfg,
    automationRegistryBaseline: serializeAutomationRegistryBaseline({
      canCreatePageOn: base.canCreatePageOn,
      agentTriggerRegistryConfig: cfg,
      actionEnabled: base.actionEnabled ?? true,
    }),
  };
}
